import { failure, isOk, ok, pending, Result } from "@/common/common";
import { ApiService, CopyShareholdersResponse, isBadRequestError } from "@/api";
import { TMessage } from "@/i18n/tt";
import { makeAutoObservable } from "mobx";
import { navigateToTransaction } from "@/utils/navigation";
import UserStore from "./UserStore";
import { History } from "history";
import { ShareTransferForm } from "@/shareholderRegister/transactions/shareTransfer/shareTransferDomain";
import { IncreaseNominalValueTransactionModel } from "@/shareholderRegister/transactions/increaseNominalValue/increaseNominalValueDomain";
import { CancellationOfSharesModel } from "@/shareholderRegister/transactions/cancellationOfShares/cancellationOfSharesDomain";
import { ConversionFromISINModel } from "@/shareholderRegister/transactions/conversionFromISIN/conversionFromISINDomain";
import { SplitModel } from "@/shareholderRegister/transactions/split/splitDomain";
import { ReverseSplitModel } from "@/shareholderRegister/transactions/reverseSplit/reverseSplitDomain";
import { ReductionOfNominalValueTransactionModel } from "@/shareholderRegister/transactions/reductionOfNominalValue/reductionOfNominalValueDomain";
import { GroupContributionFormModel } from "@/shareholderRegister/transactions/groupContribution/groupContributionDomain";
import { DividendForm } from "@/shareholderRegister/transactions/dividend/dividendDomain";
import { ConversionOfSharesModel } from "@/shareholderRegister/transactions/conversionOfShares/conversionOfSharesDomain";
import { LiquidationForm } from "@/shareholderRegister/transactions/liquidation/liquidationDomain";
import { ShareholdingPerDateModel } from "@/shareholderRegister/transactions/shareholdingPerDate/shareholdingPerDateDomain";
import { OwnersPerDateModel } from "@/shareholderRegister/transactions/ownersPerDate/ownersPerDateDomain";
import { CompanyStatusChangeForm } from "@/shareholderRegister/transactions/companyStatusChange/companyStatusChangeDomain";
import AlertMessageStore from "./AlertMessageStore";
import { TransactionType } from "@/types/enums/TransactionType";
import { IncorporationTransactionModel } from "@/shareholderRegister/transactions/issue/incorporationTransactionDomain";
import { IssueOfNewSharesTransactionModel } from "@/shareholderRegister/transactions/issue/issueTransactionDomain";
import { DemergerFormModel } from "@/shareholderRegister/transactions/demerger/demergerDomain";
import {
  ImportTransactionsResult,
  InitialTransactionState,
  TransactionApiClient,
} from "@/shareholderRegister/transactions/TransactionApiClient";
import { AxiosInstance } from "axios";
import { Transaction } from "@/types/Transactions";
import { MergerModel } from "@/shareholderRegister/transactions/merger/mergerDomain";
import { DownloadService } from "@/api/DownloadService";
import { DialogStore } from "@/dialogs/dialogDomain";
import { max, orderBy, sortBy } from "lodash";
import { isNotRegisteredIssue } from "@/shareholderRegister/transactions/transactionLogDomain";
import { diffDays, toDate } from "@/utils/Dates";
import { isFileUploadValidationError, validateFiles } from "@/fileUpload/fileUploadDomain";
import { createFileTypeValidationErrorMessage } from "@/fileUpload/fileUploadErrorMessages";
import { logError } from "@/utils/log";
import { ShareTypeHttpClient } from "@/shareholderRegister/shareClasses/shareTypeApiClient";

const TRANSACTION_SORT_ORDER: Array<keyof Transaction> = ["transactionDate", "transactionlogId"];

export class TransactionStore {
  result?: Result<number, TMessage>;
  replayError?: ReplayTransactionError;
  fetchInitialStateResult?: Result<InitialTransactionState>;
  readonly transactionApiClient: TransactionApiClient;
  readonly shareTypeHttpClient: ShareTypeHttpClient;
  showDeleteDialogForTransaction?: Transaction;
  deleteResult?: Result<TMessage, TMessage>;

  transactionLog: Transaction[] = [];
  detailedTransaction?: Transaction = undefined;
  transactionsError?: TMessage;
  transactionLogResult: Result = pending();
  changeDateResult?: Result<TMessage, TMessage>;
  changeNominalValueResult?: Result<TMessage, TMessage>;
  showChangeNominalValueDialog = false;
  selectedTransaction?: Transaction;
  isLoading = false;
  isImporting = false;

  constructor(
    readonly api: ApiService,
    readonly http: AxiosInstance,
    readonly userStore: UserStore,
    readonly messageStore: AlertMessageStore,
    readonly history: History,
    readonly downloadService: DownloadService,
    readonly dialogStore: DialogStore,
  ) {
    this.transactionApiClient = new TransactionApiClient(http);
    this.shareTypeHttpClient = new ShareTypeHttpClient(http);
    makeAutoObservable(
      this,
      { api: false, userStore: false, history: false, downloadService: false, dialogStore: false },
      { autoBind: true },
    );
  }

  *fetchInitialTransactionState(orgNo: string, date: string) {
    try {
      this.fetchInitialStateResult = pending();
      const initialState: InitialTransactionState =
        yield this.transactionApiClient.fetchInitialTransactionState(orgNo, date);
      this.fetchInitialStateResult = ok(initialState);
    } catch (e) {
      this.fetchInitialStateResult = failure();
    }
  }

  get shareNominalValue() {
    return isOk(this.fetchInitialStateResult)
      ? this.fetchInitialStateResult.data.shareNominalValue
      : undefined;
  }

  get totalNumberOfShares() {
    return isOk(this.fetchInitialStateResult)
      ? this.fetchInitialStateResult.data.totalNumberOfShares
      : undefined;
  }

  *incorporation(model: IncorporationTransactionModel, transactionId?: string) {
    this.result = undefined;
    if (!model.validate()) return;

    model.clearDirty();
    try {
      this.result = pending();
      const id: number = yield this.api.issueShares(model.prepareData(), transactionId);
      this.showSuccessPage(id);
    } catch (e) {
      this.handleError(e);
    }
  }

  *showSuccessPage(transactionId: number) {
    yield this.userStore.updateCompanyInfo();
    this.result = ok(transactionId);
    navigateToTransaction(this.userStore.orgNumber, transactionId, this.history);
    this.messageStore.showSuccess({ key: "transactions.save-success" });
  }

  private handleError(e: unknown) {
    this.result = failure({ key: "general-error" });
    if (isBadRequestError(e) && e.response?.data) {
      const error = e.response.data as TransactionErrorResponse;
      if (error.replayError !== undefined) {
        this.replayError = error.replayError;
      } else if (error.validationError !== undefined) {
        this.messageStore.showError(
          transactionValidationErrorMessage(error.validationError, { key: "general-error" }),
        );
      }
    } else {
      this.messageStore.showError({ key: "general-error" });
    }
  }

  *issueShares(model: IssueOfNewSharesTransactionModel, transactionId?: string) {
    this.result = undefined;
    if (!model.validate()) return;

    model.clearDirty();
    try {
      this.result = pending();
      const id: number = yield this.api.issueShares(model.prepareData(), transactionId);
      this.showSuccessPage(id);
    } catch (e) {
      this.handleError(e);
    }
  }

  *increaseNominalValue(model: IncreaseNominalValueTransactionModel, transactionId?: string) {
    this.result = undefined;
    if (!model.validate()) return;

    model.clearDirty();
    try {
      this.result = pending();
      const id: number = yield this.api.increaseNominalValue(model.prepareData(), transactionId);
      this.showSuccessPage(id);
    } catch (e) {
      this.handleError(e);
    }
  }

  *shareTransfer(model: ShareTransferForm, existingTransactionId?: number) {
    this.result = undefined;
    if (!model.validate()) return;

    model.clearDirty();
    try {
      this.result = pending();
      const transactionId: number = model.isSellAllShares
        ? yield this.api.transferAllShares(model.prepareDataSellAll(), existingTransactionId)
        : yield this.api.createTransfer(
            model.isMultipleSellers
              ? model.prepareDataMultipleSellers()
              : model.prepareDataNewTransaction(),
            existingTransactionId,
          );
      this.showSuccessPage(transactionId);
    } catch (e) {
      this.handleError(e);
    }
  }

  *cancelShares(model: CancellationOfSharesModel, transactionId?: string) {
    this.result = undefined;
    if (!model.validate()) return;
    model.clearDirty();
    try {
      this.result = pending();
      const id: number = yield this.api.shareCancellation(model.prepareData(), transactionId);
      this.showSuccessPage(id);
    } catch (e) {
      this.handleError(e);
    }
  }

  *split(model: SplitModel, transactionId?: number) {
    this.result = undefined;
    if (!model.validate()) return;
    model.clearDirty();
    try {
      this.result = pending();
      const id: number = yield this.api.shareSplit(model.prepareDataShareSplit(), transactionId);
      this.showSuccessPage(id);
    } catch (e) {
      this.handleError(e);
    }
  }

  *conversionFromISIN(model: ConversionFromISINModel, transactionId?: string) {
    this.result = undefined;
    if (!model.validate()) return;
    model.clearDirty();
    try {
      this.result = pending();
      const id: number = yield this.api.conversionFromISIN(model.prepareData(), transactionId);
      this.showSuccessPage(id);
    } catch (e) {
      this.handleError(e);
    }
  }

  *reverseSplit(model: ReverseSplitModel, existingTransactionId?: number) {
    this.result = undefined;
    if (!model.validate()) return;
    model.clearDirty();
    try {
      this.result = pending();
      const transactionId: number = yield this.api.shareReverseSplit(
        model.prepareReverseSplitData(),
        existingTransactionId,
      );
      this.showSuccessPage(transactionId);
    } catch (e) {
      this.handleError(e);
    }
  }

  *reduceNominalValue(model: ReductionOfNominalValueTransactionModel, transactionId?: number) {
    this.result = undefined;
    if (!model.validate()) return;
    model.clearDirty();
    try {
      this.result = pending();
      const id: number = yield this.api.reductionOfNominalValue(
        model.prepareData(),
        transactionId?.toString(),
      );
      this.showSuccessPage(id);
    } catch (e) {
      this.handleError(e);
    }
  }

  *groupContribution(model: GroupContributionFormModel, existingTransactionId?: string) {
    this.result = undefined;
    if (!model.validate()) return;
    model.clearDirty();
    try {
      this.result = pending();
      const transactionId: number = yield this.api.registerGroupContribution(
        model.prepareDataGroupContribution(),
        existingTransactionId,
      );
      this.showSuccessPage(transactionId);
    } catch (e) {
      this.handleError(e);
    }
  }

  *dividend(model: DividendForm, existingTransactionId?: number) {
    this.result = undefined;
    if (!model.validate()) return;
    model.clearDirty();
    try {
      this.result = pending();
      const transactionId: number = yield this.api.payDividend(
        model.prepareDataPayDividend(),
        existingTransactionId,
      );
      this.showSuccessPage(transactionId);
    } catch (e) {
      this.handleError(e);
    }
  }

  *conversionOfShares(model: ConversionOfSharesModel, transactionId?: string) {
    this.result = undefined;
    if (!model.validate()) return;
    model.clearDirty();
    try {
      this.result = pending();
      const id: number = yield this.api.conversionOfShares(model.prepareData(), transactionId);
      this.showSuccessPage(id);
    } catch (e) {
      this.handleError(e);
    }
  }

  *liquidation(model: LiquidationForm, transactionId?: number) {
    this.result = undefined;
    if (!model.validate()) return;
    model.clearDirty();
    try {
      this.result = pending();
      const id: number = transactionId
        ? yield this.transactionApiClient.updateTransaction(
            String(transactionId),
            model.prepareData(),
          )
        : yield this.transactionApiClient.registerLiquidation(model.prepareData());
      this.showSuccessPage(id);
    } catch (e) {
      this.handleError(e);
    }
  }

  *companyStatusChange(model: CompanyStatusChangeForm) {
    this.result = undefined;
    const data = model.prepareData();
    if (!model.validate() || !data) return;
    model.clearDirty();
    try {
      this.result = pending();
      const id: number = yield this.transactionApiClient.registerCompanyStatusChange(data);
      this.showSuccessPage(id);
    } catch (e) {
      this.handleError(e);
    }
  }

  *shareholdingPerDate(model: ShareholdingPerDateModel, transactionId?: string) {
    this.result = undefined;
    if (!model.validate()) return;
    model.clearDirty();
    try {
      this.result = pending();
      const id: number = yield this.api.shareholdingPerDate(model.prepareData(), transactionId);
      this.showSuccessPage(id);
    } catch (e) {
      this.handleError(e);
    }
  }

  *ownersPerDate(model: OwnersPerDateModel, transactionId?: string) {
    this.result = undefined;
    if (!model.validate()) return;
    model.clearDirty();
    try {
      this.result = pending();
      for (const total of model.changedVotes()) {
        yield this.shareTypeHttpClient.editShareType(
          model.shareTypeRequest(total),
          total.shareType.sharetypeId,
        );
      }
      const id: number = transactionId
        ? yield this.transactionApiClient.updateTransaction(transactionId, model.prepareData())
        : yield this.transactionApiClient.registerOwnersPerDate(model.prepareData());
      this.showSuccessPage(id);
    } catch (e) {
      this.handleError(e);
    }
  }

  clearReplayError() {
    this.replayError = undefined;
  }

  *merger(
    model: MergerModel,
    shareholderIds: number[],
    copyFromOrgNo?: string,
    transactionId?: number,
  ) {
    this.result = undefined;
    if (!model.validate()) return;
    model.clearDirty();
    try {
      this.result = pending();

      const copyResponse: CopyShareholdersResponse | undefined = copyFromOrgNo
        ? yield this.api.copyShareholders(model.orgNo, copyFromOrgNo, shareholderIds)
        : undefined;

      const payload = model.prepareData(copyResponse?.shareholderIdMapping ?? []);
      const id: number = transactionId
        ? yield this.transactionApiClient.updateTransaction(transactionId, payload)
        : yield this.transactionApiClient.registerMerger(
            payload,
            model.canRegisterForTransferringCompany &&
              (model.registerForTransferringCompany.value ?? false),
          );

      this.showSuccessPage(id);
    } catch (e) {
      this.handleError(e);
    }
  }

  *demerger(model: DemergerFormModel, transactionId?: string) {
    this.result = undefined;
    if (!model.validate()) return;
    model.clearDirty();
    try {
      this.result = pending();
      const id: number = transactionId
        ? yield this.transactionApiClient.updateTransaction(transactionId, model.prepareData())
        : yield this.transactionApiClient.registerDemerger(model.prepareData());
      this.showSuccessPage(id);
    } catch (e) {
      this.handleError(e);
    }
  }

  *deleteTransaction(transaction: Transaction, onSuccess: () => Promise<void>) {
    this.deleteResult = pending();
    try {
      yield this.api.deleteTransaction(this.userStore.orgNumber, transaction.transactionlogId);
      yield this.userStore.updateCompanyInfo();
      this.deleteResult = ok({ key: "transactions.delete.success" });
      this.messageStore.showSuccess({ key: "transactions.delete.success" });
      this.showDeleteDialogForTransaction = undefined;

      yield onSuccess();
    } catch (e) {
      if (isBadRequestError(e) && e.response?.data) {
        const error = e.response.data as TransactionErrorResponse;
        if (error.replayError !== undefined) {
          this.showDeleteDialogForTransaction = undefined;
          this.replayError = error.replayError;
        } else {
          this.deleteResult = failure({ key: "transactions.delete.system-error" });
        }
      } else {
        this.deleteResult = failure({ key: "transactions.delete.system-error" });
      }
    }
  }

  *importTransactions(file: File, language: string) {
    this.isImporting = true;
    try {
      const result: ImportTransactionsResult = yield this.transactionApiClient.importTransactions(
        this.userStore.orgNumber,
        file,
        language,
      );
      this.dialogStore.openDialog({ type: "import-result", result });
      if (result.failed.length === 0) {
        yield this.getTransactionLog();
      }
    } catch (e) {
      logError(e);
      this.dialogStore.openDialog({ type: "import-result", result: null });
    } finally {
      this.isImporting = false;
    }
  }

  openDeleteTransactionDialog(transaction: Transaction) {
    this.deleteResult = undefined;
    this.showDeleteDialogForTransaction = transaction;
  }

  closeDeleteDialog() {
    this.showDeleteDialogForTransaction = undefined;
  }

  setLoading(isLoading: boolean) {
    this.isLoading = isLoading;
  }

  setTransactionsError(error?: TMessage) {
    this.transactionsError = error;
  }

  private fetchTransactionLog(): Promise<Transaction[]> {
    return this.api.fetchTransactionLog(this.userStore.orgNumber);
  }

  *getTransactionLog() {
    try {
      this.transactionLogResult = pending();
      this.setLoading(true);
      const transactionLog: Transaction[] = yield this.fetchTransactionLog();
      this.transactionLog = orderBy(transactionLog, TRANSACTION_SORT_ORDER, ["desc", "desc"]);
      this.transactionLogResult = ok();
    } catch (e) {
      this.messageStore.showError({ key: "shareholders.error.load" });
    } finally {
      this.setLoading(false);
    }
  }

  changeTransactionDate(transaction: Transaction, infoText?: TMessage) {
    this.dialogStore.openChangeTransactionDateDialog({
      selectedTransaction: transaction,
      controller: this,
      infoText,
    });
    this.changeDateResult = undefined;
  }

  cancelChangeTransactionDate() {
    this.changeDateResult = undefined;
    this.dialogStore.closeDialog();
  }

  *doChangeTransactionDate(transaction: Transaction, date: string): Generator {
    this.changeDateResult = pending();
    try {
      yield this.api.changeTransactionDate(
        this.userStore.orgNumber,
        transaction.transactionlogId,
        date,
      );
      this.dialogStore.closeDialog();
      yield this.userStore.updateCompanyInfo();
      this.changeDateResult = ok({ key: "transactions.change-date.success" });
      this.messageStore.showSuccess({ key: "transactions.change-date.success" });
      yield this.getTransactionLog();
      if (this.detailedTransaction) {
        yield this.getDetailedTransaction(this.detailedTransaction.transactionlogId);
      }
    } catch (e) {
      const isInvalidDate =
        isBadRequestError(e) &&
        (e.response?.data as TransactionErrorResponse | undefined)?.replayError === undefined;
      this.changeDateResult = failure(
        isInvalidDate
          ? { key: "transactions.change-date.validation-error" }
          : { key: "transactions.change-date.system-error" },
      );
    }
  }

  changeNominalValue(transaction: Transaction) {
    this.selectedTransaction = transaction;
    this.showChangeNominalValueDialog = true;
    this.changeNominalValueResult = undefined;
  }

  cancelChangeNominalValue() {
    this.selectedTransaction = undefined;
    this.showChangeNominalValueDialog = false;
    this.changeNominalValueResult = undefined;
  }

  *doChangeNominalValue(transaction: Transaction, nominalValue: number) {
    this.changeNominalValueResult = pending();
    try {
      yield this.api.changeNominalValueForTransaction(
        this.userStore.orgNumber,
        transaction.transactionlogId,
        nominalValue,
      );
      this.messageStore.showSuccess({ key: "transactions.change-nominal-value.success" });
      this.changeNominalValueResult = undefined;
      this.showChangeNominalValueDialog = false;
      this.selectedTransaction = undefined;
      yield this.getTransactionLog();
      yield this.userStore.updateCompanyInfo();
    } catch (e) {
      this.changeNominalValueResult = failure({ key: "general-error" });
    }
  }

  *getDetailedTransaction(id: number | string) {
    try {
      this.setLoading(true);
      this.detailedTransaction = yield this.api.fetchTransactionById(id);
    } catch (e) {
      this.setTransactionsError({ key: "shareholders.error.load" });
    } finally {
      this.setLoading(false);
    }
  }

  *uploadTransactionAttachment(files: File[]) {
    const transaction = this.detailedTransaction;
    if (!transaction || !files.length || !transaction.fileUploadConfig) {
      return;
    }
    const id = transaction.transactionlogId;
    try {
      const validFiles: File[] = yield validateFiles(files, transaction.fileUploadConfig);
      const form = new FormData();
      validFiles.forEach((file) => form.append("files", file));
      yield this.api.uploadTransactionAttachment(id, form);
      this.messageStore.showSuccess({
        key: "transactions.upload-attachments-success-message",
        params: { count: validFiles.length, fileName: validFiles[0].name },
      });
    } catch (err) {
      if (isFileUploadValidationError(err)) {
        this.messageStore.showError(createFileTypeValidationErrorMessage(err));
      }
    } finally {
      yield this.getDetailedTransaction(id);
    }
  }

  *deleteTransactionAttachment(attachmentId: string) {
    if (this.detailedTransaction) {
      try {
        yield this.api.deleteTransactionAttachment(
          this.detailedTransaction.transactionlogId,
          attachmentId,
        );
      } catch (e) {
        logError(e);
      }
      yield this.getDetailedTransaction(this.detailedTransaction.transactionlogId);
    }
  }

  clearDetailedTransaction = () => {
    this.detailedTransaction = undefined;
  };

  downloadContractNotes() {
    if (this.detailedTransaction) {
      this.setLoading(true);
      this.downloadService
        .downloadContractNote(this.detailedTransaction.transactionlogId)
        .catch((result) => {
          if (result.status === "rejected") {
            this.setTransactionsError({ key: "shareholders.error.downloadingContractNote" });
          }
        })
        .finally(() => {
          this.setLoading(false);
        });
    }
  }

  get emptyTransactionLog() {
    return this.transactionLog.length === 0;
  }

  get firstNotRegisteredIssueDate(): string | undefined {
    return sortBy(this.transactionLog, (t) => t.transactionDate).find((t) =>
      isNotRegisteredIssue(t),
    )?.transactionDate;
  }

  isTransactionLogChangedInLastDay() {
    const updateDates = this.transactionLog.map((t) => t.updatedDate);
    const latestUpdate = max(updateDates);
    return latestUpdate ? diffDays(new Date(), toDate(latestUpdate)) <= 1 : false;
  }
}

export type TransactionErrorResponse = {
  validationError?: TransactionValidationError;
  replayError?: ReplayTransactionError;
};

export type ReplayTransactionError = {
  transactionId: number;
  organizationnumber: string;
  transactionType: TransactionType;
  transactionDate: string;
  operation: ReplayOperationType;
};

export type ReplayOperationType = "Save" | "Delete";

export const replayOperationTypeMessages: Record<ReplayOperationType, TMessage> = {
  Save: { key: "transactions.replay-failed.title-save" },
  Delete: { key: "transactions.replay-failed.title-delete" },
};

enum TransactionValidationError {
  EXISTING_TRANSACTION_ON_DATE = "EXISTING_TRANSACTION_ON_DATE",
  TRANSACTIONS_ALREADY_EXIST = "TRANSACTIONS_ALREADY_EXIST",
  NO_INITIAL_TRANSACTION = "NO_INITIAL_TRANSACTION",
  MUST_BE_AFTER_INITIAL_TRANSACTION = "MUST_BE_AFTER_INITIAL_TRANSACTION",
  ILLEGAL_SPLIT_RATIO = "ILLEGAL_SPLIT_RATIO",
  OWNERS_EXCEED_TOTAL = "OWNERS_EXCEED_TOTAL",
  INVALID_OWNERS_PER_DATE = "INVALID_OWNERS_PER_DATE",
  TRANSACTION_TYPE_NOT_ALLOWED = "TRANSACTION_TYPE_NOT_ALLOWED",
  INVALID_COMPANY_STATUS_CHANGE = "INVALID_COMPANY_STATUS_CHANGE",
  DIVIDEND_SPLIT_WITHOUT_MANUAL_CALCULATION = "DIVIDEND_SPLIT_WITHOUT_MANUAL_CALCULATION",
  DIVIDEND_SPLIT_RECIPIENTS_MISMATCH = "DIVIDEND_SPLIT_RECIPIENTS_MISMATCH",
  DIVIDEND_SPLIT_INVALID_AMOUNT = "DIVIDEND_SPLIT_INVALID_AMOUNT",
  DIVIDEND_SPLIT_SUM_MISMATCH = "DIVIDEND_SPLIT_SUM_MISMATCH",
  OPTION_EXERCISE_SHARES_LOCKED = "OPTION_EXERCISE_SHARES_LOCKED",
}

const transactionValidationErrorMessages: Record<TransactionValidationError, TMessage> = {
  [TransactionValidationError.EXISTING_TRANSACTION_ON_DATE]: {
    key: "transactions.error.existing-transaction-on-date",
  },
  [TransactionValidationError.TRANSACTIONS_ALREADY_EXIST]: {
    key: "transactions.error.transactions-already-exist",
  },
  [TransactionValidationError.NO_INITIAL_TRANSACTION]: {
    key: "transactions.error.no-initial-transaction",
  },
  [TransactionValidationError.MUST_BE_AFTER_INITIAL_TRANSACTION]: {
    key: "transactions.error.must-be-after-initial-transaction",
  },
  [TransactionValidationError.ILLEGAL_SPLIT_RATIO]: {
    key: "transactions.error.illegal-split-ratio",
  },
  [TransactionValidationError.OWNERS_EXCEED_TOTAL]: {
    key: "transactions.error.owners-exceed-total",
  },
  [TransactionValidationError.INVALID_OWNERS_PER_DATE]: {
    key: "transactions.error.invalid-owners-per-date",
  },
  [TransactionValidationError.TRANSACTION_TYPE_NOT_ALLOWED]: {
    key: "transactions.error.transaction-type-not-allowed",
  },
  [TransactionValidationError.INVALID_COMPANY_STATUS_CHANGE]: {
    key: "transactions.error.invalid-company-status-change",
  },
  [TransactionValidationError.DIVIDEND_SPLIT_WITHOUT_MANUAL_CALCULATION]: {
    key: "transactions.error.dividend-split-without-manual-calculation",
  },
  [TransactionValidationError.DIVIDEND_SPLIT_RECIPIENTS_MISMATCH]: {
    key: "transactions.error.dividend-split-recipients-mismatch",
  },
  [TransactionValidationError.DIVIDEND_SPLIT_INVALID_AMOUNT]: {
    key: "transactions.error.dividend-split-invalid-amount",
  },
  [TransactionValidationError.DIVIDEND_SPLIT_SUM_MISMATCH]: {
    key: "transactions.error.dividend-split-sum-mismatch",
  },
  [TransactionValidationError.OPTION_EXERCISE_SHARES_LOCKED]: {
    key: "transactions.error.option-exercise-shares-locked",
  },
};

// The value arrives as JSON, so a validation error the backend added and the enum above has not
// caught up with reaches this lookup as an unknown string.
export function transactionValidationErrorMessage(error: string, fallback: TMessage): TMessage {
  const messages: Record<string, TMessage | undefined> = transactionValidationErrorMessages;
  return messages[error] ?? fallback;
}
